import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    trend: "up" | "down" | "neutral";
    period: string;
  };
  icon: React.ComponentType<any>;
  color?:
    | "blue"
    | "green"
    | "yellow"
    | "red"
    | "purple"
    | "indigo"
    | "gray"
    | "emerald"
    | "teal"
    | "cyan"
    | "pink"
    | "rose";
  gradient?: boolean;
  onClick?: () => void;
  loading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  color = "blue",
  gradient = false,
  onClick,
  loading = false,
}) => {
  const colorClasses = {
    blue: {
      background: "bg-blue-50",
      text: "text-blue-600",
      iconBg: "bg-blue-500",
      gradient: "bg-gradient-to-br from-blue-500 to-blue-600",
    },
    green: {
      background: "bg-green-50",
      text: "text-green-600",
      iconBg: "bg-green-500",
      gradient: "bg-gradient-to-br from-green-500 to-green-600",
    },
    yellow: {
      background: "bg-yellow-50",
      text: "text-yellow-600",
      iconBg: "bg-yellow-500",
      gradient: "bg-gradient-to-br from-yellow-500 to-yellow-600",
    },
    red: {
      background: "bg-red-50",
      text: "text-red-600",
      iconBg: "bg-red-500",
      gradient: "bg-gradient-to-br from-red-500 to-red-600",
    },
    purple: {
      background: "bg-purple-50",
      text: "text-purple-600",
      iconBg: "bg-purple-500",
      gradient: "bg-gradient-to-br from-purple-500 to-purple-600",
    },
    indigo: {
      background: "bg-indigo-50",
      text: "text-indigo-600",
      iconBg: "bg-indigo-500",
      gradient: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    },
    gray: {
      background: "bg-gray-50",
      text: "text-gray-600",
      iconBg: "bg-gray-500",
      gradient: "bg-gradient-to-br from-gray-500 to-gray-600",
    },
    emerald: {
      background: "bg-emerald-50",
      text: "text-emerald-600",
      iconBg: "bg-emerald-500",
      gradient: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    },
    teal: {
      background: "bg-teal-50",
      text: "text-teal-600",
      iconBg: "bg-teal-500",
      gradient: "bg-gradient-to-br from-teal-500 to-teal-600",
    },
    cyan: {
      background: "bg-cyan-50",
      text: "text-cyan-600",
      iconBg: "bg-cyan-500",
      gradient: "bg-gradient-to-br from-cyan-500 to-cyan-600",
    },
    pink: {
      background: "bg-pink-50",
      text: "text-pink-600",
      iconBg: "bg-pink-500",
      gradient: "bg-gradient-to-br from-pink-500 to-pink-600",
    },
    rose: {
      background: "bg-rose-50",
      text: "text-rose-600",
      iconBg: "bg-rose-500",
      gradient: "bg-gradient-to-br from-rose-500 to-rose-600",
    },
  };

  const changeColors = {
    up: "text-green-600",
    down: "text-red-600",
    neutral: "text-gray-600",
  };

  const changeIcons = {
    up: TrendingUp,
    down: TrendingDown,
    neutral: Minus,
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-3 sm:p-4 lg:p-6 animate-pulse">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
          <div className="flex-1 min-w-0">
            <div className="w-20 sm:w-24 h-3 sm:h-4 bg-gray-200 rounded mb-1 sm:mb-2"></div>
            <div className="w-12 sm:w-16 h-4 sm:h-5 lg:h-6 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border p-3 sm:p-4 lg:p-6 hover:shadow-md transition-all duration-200 ${
        onClick ? "cursor-pointer" : ""
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                gradient
                  ? colorClasses[color].gradient
                  : colorClasses[color].background
              }`}
            >
              <Icon
                className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                {title}
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                {value}
              </p>
            </div>
          </div>
          {change && (
            <div className="mt-2 sm:mt-3 lg:mt-4 flex items-center gap-1 sm:gap-2">
              {React.createElement(changeIcons[change.trend], {
                className: `w-3 h-3 sm:w-4 sm:h-4 ${
                  changeColors[change.trend]
                }`,
              })}
              <span
                className={`text-xs sm:text-sm font-medium ${
                  changeColors[change.trend]
                } truncate`}
              >
                {change.value > 0 ? "+" : ""}
                {change.value}% {change.period}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
